import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { costCentersApi, type CostCenter } from '@/api';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

type CostCenterSortColumn = 'code' | 'name' | 'active';

export function CentrosCustoPage() {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  const [sort, setSort] = useState<SortState<CostCenterSortColumn>>({ column: 'code', dir: 'asc' });
  const toggleSort = makeToggleSort(setSort);

  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [toggling, setToggling] = useState<CostCenter | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cost-centers'],
    queryFn: costCentersApi.list,
  });

  const sortedItems = useMemo(() => {
    const rows = data ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'code':   primary = cmp.text(a.code, b.code); break;
        case 'name':   primary = cmp.text(a.name, b.name); break;
        case 'active': primary = cmp.bool(a.active, b.active); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.text(a.code, b.code);
    });
  }, [data, sort]);

  function refresh() {
    qc.invalidateQueries({ queryKey: ['cost-centers'] });
  }

  return (
    <>
      <PageHeader
        title="Centros de custo"
        breadcrumb="Cadastros"
        actions={
          isAdmin && (
            <Button size="sm" onClick={() => setOpenNew(true)}>
              <Plus className="w-4 h-4" /> Novo
            </Button>
          )
        }
      />
      <PageBody>
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="code"   label="Código" sort={sort} onClick={toggleSort} />
                <SortHeader column="name"   label="Nome"   sort={sort} onClick={toggleSort} />
                <SortHeader column="active" label="Status" sort={sort} onClick={toggleSort} />
                {isAdmin && <th className="px-3 py-2 font-medium w-20 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && (
                <tr>
                  <td colSpan={isAdmin ? 4 : 3} className="px-3 py-6 text-center text-muted-foreground">
                    Carregando…
                  </td>
                </tr>
              )}
              {sortedItems.map((cc) => (
                <tr key={cc.id} className="group">
                  <td className="px-3 py-2 font-mono text-xs">{cc.code}</td>
                  <td className="px-3 py-2">{cc.name}</td>
                  <td className="px-3 py-2">
                    {cc.active
                      ? <Badge variant="success">Ativo</Badge>
                      : <Badge variant="danger">Inativo</Badge>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditing(cc)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          title="Editar"
                          aria-label="Editar centro de custo"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setToggling(cc)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                          title={cc.active ? 'Inativar' : 'Reativar'}
                          aria-label={cc.active ? 'Inativar centro de custo' : 'Reativar centro de custo'}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!isLoading && (data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 4 : 3} className="px-3 py-6 text-center text-muted-foreground">
                    Nenhum centro de custo cadastrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          Centros de custo agrupam lançamentos por área, projeto ou unidade. Não é possível excluir
          um centro de custo — para ocultá-lo dos seletores, inative-o.
        </p>
      </PageBody>

      {openNew && (
        <CostCenterFormDialog
          editing={null}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); refresh(); }}
        />
      )}
      {editing && (
        <CostCenterFormDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {toggling && (
        <ToggleActiveDialog
          target={toggling}
          onClose={() => setToggling(null)}
          onDone={() => { setToggling(null); refresh(); }}
        />
      )}
    </>
  );
}

// ===================== Form (criar / editar) =====================

function CostCenterFormDialog({
  editing,
  onClose,
  onSaved,
}: {
  editing: CostCenter | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [code, setCode] = useState(editing?.code ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [active, setActive] = useState(editing?.active ?? true);

  const create = useMutation({
    mutationFn: () => costCentersApi.create({ code, name }),
    onSuccess: onSaved,
  });
  const update = useMutation({
    mutationFn: () => costCentersApi.update(editing!.id, { code, name, active }),
    onSuccess: onSaved,
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    mut.mutate();
  }

  return (
    <Dialog open onClose={onClose} title={isEdit ? `Editar centro de custo (${editing!.code})` : 'Novo centro de custo'}>
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label>Código</Label>
            <Input
              autoFocus
              maxLength={40}
              placeholder="Ex.: ADM, OP01, OBRA-CENTRO…"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <p className="text-xs text-muted-foreground">
              Sigla curta usada para identificar o centro nos relatórios.
            </p>
          </div>
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input
              maxLength={120}
              placeholder="Ex.: Administrativo, Obra Centro…"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-input"
              />
              Ativo
            </label>
          )}
          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!code.trim() || !name.trim() || mut.isPending}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ===================== Toggle ativa/inativa =====================

function ToggleActiveDialog({
  target,
  onClose,
  onDone,
}: {
  target: CostCenter;
  onClose: () => void;
  onDone: () => void;
}) {
  const willActivate = !target.active;
  const mut = useMutation({
    mutationFn: () => costCentersApi.update(target.id, { active: willActivate }),
    onSuccess: onDone,
  });
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  return (
    <Dialog open onClose={onClose} title={willActivate ? 'Reativar centro de custo' : 'Inativar centro de custo'}>
      <DialogBody className="space-y-3">
        <p>
          {willActivate ? 'Reativar' : 'Inativar'} o centro <strong>{target.name}</strong>{' '}
          <span className="font-mono text-xs text-muted-foreground">({target.code})</span>?
        </p>
        <p className="text-sm text-muted-foreground">
          {willActivate
            ? 'Voltará a aparecer nos seletores de lançamentos.'
            : 'Deixará de aparecer nos seletores de novos lançamentos. Os lançamentos existentes permanecem inalterados.'}
        </p>
        {apiError && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
            {apiError}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant={willActivate ? 'default' : 'destructive'}
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Salvando…' : willActivate ? 'Reativar' : 'Inativar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
